import React, { memo, useState } from 'react';
import { Handle, Position } from 'reactflow';
import { FaCog, FaTrash, FaCopy, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';

const CustomNode = ({ id, data, selected, ...props }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [showProgress, setShowProgress] = useState(false);

  const handleEdit = () => {
    if (data.onEdit) {
      data.onEdit(id);
    }
  };

  const handleDelete = () => {
    if (data.onDelete) {
      data.onDelete(id);
    }
  };

  const handleDuplicate = () => {
    if (data.onDuplicate) {
      data.onDuplicate(id);
    }
  };

  // Show progress when executing
  React.useEffect(() => {
    if (data.isExecuting) {
      setShowProgress(true);
      const timer = setTimeout(() => setShowProgress(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [data.isExecuting]);

  const getNodeBorderColor = () => {
    if (selected) return '#5e35b1'; // Flowise purple accent
    
    switch (data.category) {
      case 'trigger': return '#10B981';
      case 'action': return '#3B82F6';
      case 'logic': return '#F59E0B';
      case 'function': return '#8B5CF6';
      case 'social': return '#E4405F';
      case 'ecommerce': return '#96BF47';
      case 'ai': return '#9333EA';
      default: return '#d1d5db'; // Lighter default border
    }
  };

  const getNodeBackground = () => {
    if (data.isExecuting) return 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)';
    if (data.isExecuted) return 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)';
    if (selected) return 'linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%)';
    return '#ffffff';
  };

  const getConnectionStatus = () => {
    if (data.connected) {
      return { icon: <FaCheckCircle />, color: '#10B981', status: 'connected' };
    } else if (data.requiresConnection) {
      return { icon: <FaExclamationTriangle />, color: '#F59E0B', status: 'warning' };
    }
    return null;
  };

  const connectionStatus = getConnectionStatus();

  return (
    <div 
      className={`custom-node flowise-node ${selected ? 'selected' : ''} ${data.category || ''} ${data.isExecuting ? 'executing' : ''} ${data.isExecuted ? 'executed' : ''}`}
      style={{ 
        borderColor: getNodeBorderColor(),
        background: getNodeBackground(),
        boxShadow: selected 
          ? '0 0 6px 2px rgba(94, 53, 177, 0.2)' 
          : '0 2px 8px rgba(0, 0, 0, 0.1)'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Input Handle */}
      {data.hasInput && (
        <Handle
          type="target"
          position={Position.Left}
          className="flowise-handle flowise-handle-input"
          style={{
            background: selected ? '#5e35b1' : '#6b7280',
            border: '2px solid #ffffff',
            width: '12px',
            height: '12px',
            borderRadius: '3px',
            left: '-6px',
            top: '50%',
            transform: 'translateY(-50%)',
          }}
        />
      )}

      {/* Node Header */}
      <div className="flowise-node-header">
        <div className="flowise-node-avatar" style={{ background: getNodeBorderColor() }}>
          <div className="flowise-node-icon">
            {data.icon}
          </div>
        </div>
        <div className="flowise-node-content">
          <div className="flowise-node-title">{data.label}</div>
          {data.subtitle && (
            <div className="flowise-node-subtitle">{data.subtitle}</div>
          )}
        </div>
        
        {/* Connection Status */}
        {connectionStatus && (
          <div 
            className="flowise-connection-status"
            style={{ color: connectionStatus.color }}
            title={connectionStatus.status}
          >
            {connectionStatus.icon}
          </div>
        )}
      </div>

      {/* Node Content */}
      {data.description && (
        <div className="custom-node-content">
          <p>{data.description}</p>
        </div>
      )}

      {/* Node Configuration Preview */}
      {data.configuration && Object.keys(data.configuration).length > 0 && (
        <div className="node-config-preview">
          {Object.entries(data.configuration).slice(0, 2).map(([key, value]) => (
            <div key={key} className="config-item">
              <span className="config-key">{key}:</span>
              <span className="config-value">
                {String(value).length > 20 ? `${String(value).substring(0, 20)}...` : String(value)}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Execution Progress */}
      {data.isExecuting && showProgress && (
        <div className="node-progress">
          <div className="progress-bar">
            <div className="progress-fill"></div>
          </div>
          <span className="progress-text">Executing...</span>
        </div>
      )}

      {/* Execution Success Badge */}
      {data.isExecuted && (
        <div className="execution-badge success">
          <FaCheckCircle />
          <span>Completed</span>
        </div>
      )}

      {/* Node Controls */}
      {(isHovered || selected) && (
        <div className="node-controls">
          <button
            className="node-control-btn edit-btn"
            onClick={handleEdit}
            title="Configure"
          >
            <FaCog />
          </button>
          <button
            className="node-control-btn duplicate-btn"
            onClick={handleDuplicate}
            title="Duplicate"
          >
            <FaCopy />
          </button>
          <button
            className="node-control-btn delete-btn"
            onClick={handleDelete}
            title="Delete"
          >
            <FaTrash />
          </button>
        </div>
      )}

      {/* Output Handle */}
      {data.hasOutput && (
        <Handle
          type="source"
          position={Position.Right}
          className="flowise-handle flowise-handle-output"
          style={{
            background: selected ? '#5e35b1' : getNodeBorderColor(),
            border: '2px solid #ffffff',
            width: '12px',
            height: '12px',
            borderRadius: '3px',
            right: '-6px',
            top: '50%',
            transform: 'translateY(-50%)',
          }}
        />
      )}

      {/* Category Badge */}
      {data.category && (
        <div className={`category-badge ${data.category}`}>
          {data.category}
        </div>
      )}
    </div>
  );
};

export default memo(CustomNode);